import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CardBody, Col, FormGroup, FormText, Label, Row } from 'reactstrap';
import { Field } from 'redux-form';
import Brick from './BrickCore';
import { LoginForm, RegisterForm } from '../../components';
import { logOut } from '../../redux';
import {
    buildAuthKronosLoginContextPatch,
    resolveAuthKronosUserId,
} from './authKronosLoginContext';

const AUTH_MODES = ['both', 'login', 'register'];
const I18N_PREFIX = 'workflows.bricks.auth.kronos.login';

const normalizeMode = (mode) => {
    const value = String(mode || 'both').toLowerCase();
    return AUTH_MODES.includes(value) ? value : 'both';
};

const AuthKronosLoginRunner = ({ brick, context, onDone, t }) => {
    const dispatch = useDispatch();
    const { user, isAuthenticating, isRegistering } = useSelector((state) => state.auth);
    const { instanceInfos } = useSelector((state) => state.tktInstances);
    const doneRef = useRef(false);
    const previousUserIdRef = useRef(resolveAuthKronosUserId(user));
    const sawRegisteringRef = useRef(false);
    const sawAuthenticatingRef = useRef(false);

    const mode = normalizeMode(brick.getOption('mode', 'both'));
    const skipIfLoggedIn = String(brick.getOption('skip_if_logged_in', 'true')).toLowerCase() !== 'false';
    const showLogout = String(brick.getOption('show_logout', 'true')).toLowerCase() !== 'false';
    const tktInstanceId = instanceInfos?.id;

    useEffect(() => {
        if (isRegistering)
            sawRegisteringRef.current = true;
    }, [isRegistering]);

    useEffect(() => {
        if (isAuthenticating)
            sawAuthenticatingRef.current = true;
    }, [isAuthenticating]);

    const finish = (authUser) => {
        const userId = resolveAuthKronosUserId(authUser);
        if (doneRef.current || !userId)
            return;
        doneRef.current = true;

        const patch = buildAuthKronosLoginContextPatch(authUser, {
            fromRegister: sawRegisteringRef.current,
            fromLogin: sawAuthenticatingRef.current && !sawRegisteringRef.current,
        });
        Object.assign(context, patch);
        onDone(context, () => {});
    };

    useEffect(() => {
        if (!resolveAuthKronosUserId(user))
            return;
        if (skipIfLoggedIn)
            finish(user);
    }, [user?.id, skipIfLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const previousId = previousUserIdRef.current;
        const currentId = resolveAuthKronosUserId(user);
        previousUserIdRef.current = currentId;
        if (!currentId || doneRef.current || skipIfLoggedIn)
            return;
        // Newly authenticated while forms were shown → continue.
        if (!previousId && currentId)
            finish(user);
    }, [user?.id, skipIfLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleContinue = () => {
        finish(user);
    };

    const handleLogout = () => {
        sawRegisteringRef.current = false;
        sawAuthenticatingRef.current = false;
        dispatch(logOut());
    };

    if (resolveAuthKronosUserId(user) && skipIfLoggedIn)
        return null;

    if (resolveAuthKronosUserId(user) && !skipIfLoggedIn) {
        return (
            <div className="AuthKronosLoginBrick">
                <div className="d-flex flex-column align-items-end mb-3">
                    <h6 className="mb-2">
                        <i className="fa fa-user" />&nbsp;{user.fullname || user.email}
                    </h6>
                    {showLogout && (
                        <Button onClick={handleLogout} size="sm" color="warning" className="mb-2">
                            <i className="fa fa-sign-out" />&nbsp;
                            {t(`${I18N_PREFIX}.logout`)}
                        </Button>
                    )}
                    <Button color="success" onClick={handleContinue}>
                        {t(`${I18N_PREFIX}.continue`)}
                    </Button>
                </div>
            </div>
        );
    }

    const showLogin = mode === 'both' || mode === 'login';
    const showRegister = mode === 'both' || mode === 'register';
    const colSize = mode === 'both' ? 6 : 12;

    return (
        <div className="AuthKronosLoginBrick">
            <Row>
                {showLogin && (
                    <Col sm={12} lg={colSize}>
                        <Card className="loginCard">
                            <CardBody>
                                <h3>{t('login.title')}</h3>
                                <LoginForm hide_logo />
                            </CardBody>
                        </Card>
                    </Col>
                )}
                {showRegister && (
                    <Col sm={12} lg={colSize}>
                        <Card className="registerCard">
                            <CardBody>
                                <h3>{t('register.title')}</h3>
                                <RegisterForm tkt_instance_id={tktInstanceId} hide_logo />
                            </CardBody>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );
};

/** Workflow brick: Kronos login and/or register (same UX as application forms). */
class AuthKronosLoginBrick extends Brick {
    /**
     * @returns {string|null}
     */
    getNodeDetails(detailCtx) {
        const mode = normalizeMode(this.getOption('mode', 'both'));
        const t = detailCtx?.t;
        if (typeof t === 'function')
            return t(`${I18N_PREFIX}.modes.${mode}`);
        return mode;
    }

    getOptionsForm(props) {
        const { t } = props;

        return (
            <>
                <FormGroup row>
                    <Label sm={12}>{t(`${I18N_PREFIX}.options.mode`)}</Label>
                    <Col sm={12}>
                        <Field
                            component="select"
                            name={`bricks[${this.position}].options.mode`}
                            className="form-control"
                        >
                            {AUTH_MODES.map((mode) => (
                                <option key={mode} value={mode}>
                                    {t(`${I18N_PREFIX}.modes.${mode}`)}
                                </option>
                            ))}
                        </Field>
                        <FormText color="muted">
                            {t(`${I18N_PREFIX}.options.mode_help`)}
                        </FormText>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label sm={12}>{t(`${I18N_PREFIX}.options.skip_if_logged_in`)}</Label>
                    <Col sm={12}>
                        <Field
                            component="select"
                            name={`bricks[${this.position}].options.skip_if_logged_in`}
                            className="form-control"
                        >
                            <option value="true">{t('common.yes')}</option>
                            <option value="false">{t('common.no')}</option>
                        </Field>
                        <FormText color="muted">
                            {t(`${I18N_PREFIX}.options.skip_if_logged_in_help`)}
                        </FormText>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label sm={12}>{t(`${I18N_PREFIX}.options.show_logout`)}</Label>
                    <Col sm={12}>
                        <Field
                            component="select"
                            name={`bricks[${this.position}].options.show_logout`}
                            className="form-control"
                        >
                            <option value="true">{t('common.yes')}</option>
                            <option value="false">{t('common.no')}</option>
                        </Field>
                        <FormText color="muted">
                            {t(`${I18N_PREFIX}.options.show_logout_help`)}
                        </FormText>
                    </Col>
                </FormGroup>
            </>
        );
    }

    run(context, _isEmbedded, onDone) {
        const brick = this;
        const Runner = () => {
            const { t } = useTranslation();
            return (
                <AuthKronosLoginRunner
                    brick={brick}
                    context={context}
                    onDone={onDone}
                    t={t}
                />
            );
        };
        return <Runner />;
    }
}

export default AuthKronosLoginBrick;
